import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle } from 'lucide-react';
import { BillingCard } from './BillingCard';

interface ResultsSummaryCardProps {
  successfulRows: number;
  failedRows: number;
  totalProcessed: number;
  companyName?: string;
  processingType?: string;
  actualCost?: number;
  estimatedCost?: number;
  maxAuthorizedCost?: number;
  fileName?: string;
  elapsedTime?: string | null;
}

export const ResultsSummaryCard: React.FC<ResultsSummaryCardProps> = ({
  successfulRows,
  failedRows,
  totalProcessed,
  companyName,
  processingType,
  actualCost,
  estimatedCost,
  maxAuthorizedCost,
  fileName,
  elapsedTime,
}) => {
  const successRate = useMemo(() => {
    if (totalProcessed === 0) {
      return 0;
    }
    return Math.round((successfulRows / totalProcessed) * 100);
  }, [successfulRows, totalProcessed]);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="lg:col-span-2 ca-card p-4"
    >
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Batch Completed</h1>
          <p className="text-sm text-slate-600 mt-1">
            {companyName ?? 'CollectABILITY Customer'} · {processingType ?? 'Processing'}
          </p>
        </div>
        <div className="flex items-center gap-2 text-sm text-green-700">
          <CheckCircle className="w-5 h-5" />
          Payment Captured
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-center">
          <div className="text-xs text-slate-500 uppercase">Successful Rows</div>
          <div className="text-2xl font-bold text-green-600 mt-1">
            {successfulRows.toLocaleString()}
          </div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-center">
          <div className="text-xs text-slate-500 uppercase">Failed Rows</div>
          <div className="text-2xl font-bold text-red-500 mt-1">
            {failedRows.toLocaleString()}
          </div>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-center">
          <div className="text-xs text-slate-500 uppercase">Success Rate</div>
          <div className="text-2xl font-bold text-slate-600 mt-1">{successRate}%</div>
        </div>
        {elapsedTime && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-center">
            <div className="text-xs text-slate-500 uppercase">Processing Time</div>
            <div className="text-2xl font-bold font-mono text-blue-600 mt-1">{elapsedTime}</div>
          </div>
        )}
      </div>

      <BillingCard
        actualCost={actualCost}
        estimatedCost={estimatedCost}
        maxAuthorizedCost={maxAuthorizedCost}
        fileName={fileName}
        totalProcessed={totalProcessed}
      />
    </motion.div>
  );
};
